/*
  Mije Seoul — display-currency engine.

  DISPLAY ONLY: server renders SGD everywhere; every price element carries
  data-base (cents) via the mije-money snippet (or the JS renderers). This
  module reformats those elements into the chosen currency (SGD/MYR/THB/VND)
  and keeps late-added nodes converted via a MutationObserver. Checkout is
  always SGD — the cart honesty note appears whenever non-SGD is active,
  and checkout-button labels never carry amounts.

  Rates: open.er-api.com (free, no key; rates refresh daily — the published
  fix is roughly the previous UTC day). Cached in localStorage per UTC date:
  one fetch per user per day. Any failure or unsupported currency → silent
  SGD fallback and the selector shows SGD.

  Country auto-selection: with no stored choice, the currency is picked from
  the visitor's country (MY→MYR, TH→THB, VN→VND, SG and everything else→SGD).
  The country comes from Shopify's own /browsing_context_suggestions.json,
  kicked off in <head> by the mije-currency-boot snippet and cached in
  sessionStorage. PRECEDENCE: an explicit choice (localStorage mije.currency.v1,
  written only by the modal) always outranks geo and is never re-overridden —
  including an explicit choice of SGD.

  COUNTRY vs CURRENCY: these are resolved separately on purpose. The country is
  a fact about where the visitor is; the currency is a preference about what
  they read. An explicit currency choice still wins for prices forever, but it
  no longer cancels the country lookup, because the cart's delivery meter keys
  off the country (delivery policy is geography) and must stay correct for a
  shopper who prefers to read prices in another currency. MijeCurrency.country()
  is the single source of truth for that — nothing else may issue its own
  lookup.
*/

(function () {
  'use strict';

  /* The ACTIVE market context country, stamped on <html> by theme.liquid
     (layout). This is the country whose price list rendered every data-base
     value on this page — 'SG' until a /localization POST moves it. AUD
     conversion is gated on it: the pinned AUD rate assumes the +40%
     AU-context price is underneath, so applying it to an SG-context (base)
     page would under-display the real AU charge by exactly that 40%.
     MYR is gated the same way: the pinned MYR rate assumes the +7%
     MY-context (Malaysia price list) price underneath. */
  var CTX = (document.documentElement.getAttribute('data-mije-ctx') || '').toUpperCase();

  var KEY = 'mije.currency.v1'; /* explicit manual choice — the authority */
  var GKEY = 'mije.geo.v1'; /* sessionStorage: detected country, '' = failed */
  var RKEY = 'mije.fx.v1';
  var SUPPORTED = ['SGD', 'MYR', 'THB', 'VND', 'AUD'];
  /* Country → display currency. Anything absent (incl. SG) → SGD.
     AU and MY are NOT in this map on purpose — they are gated on market
     context in currencyForCountry(), because AUD/MYR must never format base
     (SG-context) prices. */
  var GEO_CURRENCY = { TH: 'THB', VN: 'VND' };
  /* Context country -> that market's own display currency. Used by the
     context-currency guard in effective(): a page rendering a FOREIGN
     market's catalog (stale cookie, jockeyed ?country=, the one-view window
     before the boot snippet's context restore lands) must never show a bare
     '$' — the figure is that market's catalog price, not the visitor's SGD
     price, so it renders in the context market's own currency instead. */
  var CTX_CURRENCY = { AU: 'AUD', MY: 'MYR', TH: 'THB', VN: 'VND' };
  /* Which switcher rows each detected region is offered: exactly its own
     market currency — one currency per geo. Cross-currency reference
     toggling is deliberately withdrawn (display integrity: every figure a
     shopper is offered is the basis of what they will be charged). An
     unknown or unlisted country — and a failed lookup — keeps every row
     (current default). Option filtering only — no price logic lives here. */
  var REGION_ROWS = {
    AU: ['AUD'],
    SG: ['SGD'],
    MY: ['MYR'],
    TH: ['THB'],
    VN: ['VND'],
  };
  // whole-unit display, no decimals (VND has 0 minor units natively; THB is
  // shown whole-baht by merchant preference)
  var ZERO_DECIMAL = ['VND', 'THB'];
  /* AUD deliberately formats under en-SG, not en-AU: an Australian-locale
     formatter emits the bare '$', indistinguishable from the SGD prices the
     rest of the page shows. A non-AU locale emits the international 'A$'
     prefix (A$49.95), which is the disambiguation AU shoppers need. Grouping
     and decimals are identical under both locales; SGD is untouched. */
  var LOCALES = { SGD: 'en-SG', MYR: 'ms-MY', THB: 'th-TH', VND: 'vi-VN', AUD: 'en-SG' };

  var sgdFormatter = new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' });
  var formatters = { SGD: sgdFormatter };
  var rates = null; // { MYR: x, THB: y, VND: z } per 1 SGD

  function store(k, v) {
    try { localStorage.setItem(k, v); } catch (e) { /* private mode */ }
  }
  function read(k) {
    try { return localStorage.getItem(k); } catch (e) { return null; }
  }
  function sread(k) {
    try { return sessionStorage.getItem(k); } catch (e) { return null; }
  }
  function sstore(k, v) {
    try { sessionStorage.setItem(k, v); } catch (e) { /* private mode */ }
  }

  /* The visitor's explicit choice, or null if they have never chosen. */
  function chosen() {
    var c = read(KEY);
    return SUPPORTED.indexOf(c) !== -1 ? c : null;
  }

  var geoCurrency = null; /* auto-selected from country; never persisted to KEY */
  /* The detected country. null = not resolved yet, '' = resolved to unknown
     (the lookup failed or returned nothing). Consumers must tell those apart:
     null means "wait", '' means "there is no answer and there will not be". */
  var geoCountry = null;

  function current() {
    return chosen() || geoCurrency || 'SGD';
  }

  function currencyForCountry(code) {
    code = String(code || '').toUpperCase();
    /* AU auto-selects AUD only once the page is actually rendering AU-context
       (+40%) prices; on the first pageview of a session the context is still
       SG and the shopper sees SGD — the charge basis for that page — until
       the boot snippet's context re-assert lands on the next view. */
    if (code === 'AU') return CTX === 'AU' ? 'AUD' : 'SGD';
    /* Same shape for MY: MYR auto-selects only once the page is rendering
       MY-context (+7%) prices; until the boot snippet's re-assert lands the
       shopper sees SGD — the charge basis for the page in front of them. */
    if (code === 'MY') return CTX === 'MY' ? 'MYR' : 'SGD';
    return GEO_CURRENCY[code] || 'SGD';
  }

  /* Derive the auto-selected currency from the country we currently hold.
     A manual choice parks it at null so current() falls straight through to
     chosen() — that is how geo is prevented from re-overriding a switch, even
     one made while the lookup was still in flight. */
  function syncGeoCurrency() {
    geoCurrency = (chosen() || !geoCountry) ? null : currencyForCountry(geoCountry);
  }

  /*
    Country resolution that needs no network: already resolved earlier in this
    session. Returns true when settled — that is the no-flash path, because it
    lands before the first apply() rather than a tick after it.
  */
  function resolveCountrySync() {
    var cached = sread(GKEY);
    if (cached === null) return false;
    geoCountry = cached;
    return true;
  }

  /*
    Resolve the country. Never rejects, never throws: any failure settles it to
    '', which means SGD prices and the meter's neutral delivery line.

    Runs even when the visitor has chosen a currency — see the COUNTRY vs
    CURRENCY note at the top. The lookup itself normally already started in
    <head> (window.__mijeGeo); this only re-issues it if that never ran.
  */
  function resolveCountry() {
    if (resolveCountrySync()) return Promise.resolve(geoCountry);

    var pending = window.__mijeGeo;
    if (!pending) {
      if (typeof fetch !== 'function') {
        geoCountry = '';
        return Promise.resolve('');
      }
      pending = fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          var c = d && d.detected_values && d.detected_values.country && d.detected_values.country.handle;
          c = typeof c === 'string' ? c.toUpperCase() : '';
          sstore(GKEY, c);
          return c;
        });
    }

    return pending
      .then(function (code) {
        geoCountry = typeof code === 'string' ? code.toUpperCase() : '';
        return geoCountry;
      })
      .catch(function () {
        geoCountry = '';
        return '';
      });
  }

  function effective() {
    var c = current();
    /* AUD only ever formats AU-context prices — see the CTX note above. A
       stale manual AUD choice on an SG-context page falls back to SGD, which
       is the truthful charge basis for that page. */
    if (c === 'AUD' && CTX !== 'AU') c = 'SGD';
    /* Same gate for MYR: the pinned MYR rate assumes the +7% MY-context
       price list underneath — never apply it to base (SG-context) prices. */
    if (c === 'MYR' && CTX !== 'MY') c = 'SGD';
    if (c !== 'SGD' && !(rates && rates[c])) c = 'SGD'; // silent fallback
    /* Context-currency guard: this page is rendering a FOREIGN market's
       catalog and the resolved display currency would be SGD (geo is not
       the context country — stale cookie, jockeyed param, or the one-view
       window before the boot restore lands). Render in the context market's
       own currency so a foreign-catalog figure is never mistaken for an SGD
       price. Overrides even an explicit SGD choice — "never a bare $ on a
       foreign catalog" is absolute. Degrades to SGD only if no rate source
       is available at all (pinned metafield AND er-api both dead). */
    if (c === 'SGD' && CTX_CURRENCY[CTX] && String(geoCountry || '') !== CTX) {
      var cc = CTX_CURRENCY[CTX];
      if (rates && rates[cc]) return cc;
    }
    return c;
  }

  function formatterFor(cur) {
    if (!formatters[cur]) {
      var opts = { style: 'currency', currency: cur };
      if (ZERO_DECIMAL.indexOf(cur) !== -1) {
        opts.minimumFractionDigits = 0;
        opts.maximumFractionDigits = 0;
      }
      formatters[cur] = new Intl.NumberFormat(LOCALES[cur] || 'en-SG', opts);
    }
    return formatters[cur];
  }

  /* Retail price points, applied to the DISPLAYED amount only — the rate
     itself stays exact. Always rounds UP, so rounding can only widen the
     displayed >= charged margin, never break it.
       MYR: up to the next .90 ending      142.24 -> 142.90
       AUD: up to the next .95 ending      49.12  -> 49.95
       THB: up to the next whole baht ending in 9 or 0   1183.2 -> 1189
       VND: up to the next 1,000 dong step  912741 -> 913000 */
  function retailRound(cur, amount) {
    if (cur === 'MYR') {
      var base = Math.floor(amount);
      return (amount <= base + 0.9 ? base : base + 1) + 0.9;
    }
    if (cur === 'THB') {
      var n = Math.ceil(amount);
      while (n % 10 !== 9 && n % 10 !== 0) n++;
      return n;
    }
    if (cur === 'VND') return Math.ceil(amount / 1000) * 1000;
    if (cur === 'AUD') {
      var b = Math.floor(amount);
      return (amount <= b + 0.95 ? b : b + 1) + 0.95;
    }
    return amount;
  }

  function format(sgdCents) {
    var cur = effective();
    if (cur === 'SGD') return sgdFormatter.format(sgdCents / 100);
    return formatterFor(cur).format(retailRound(cur, (sgdCents / 100) * rates[cur]));
  }

  /* Region-filter the switcher's option rows by DETECTED country (not by
     context, not by preference). Runs from apply() so it settles as soon as
     the geo lookup lands. Countries with no REGION_ROWS entry — and a
     failed/unresolved lookup — keep every row: the current default. */
  function filterRows() {
    var allowed = REGION_ROWS[String(geoCountry || '').toUpperCase()] || null;
    document.querySelectorAll('[data-currency-radio]').forEach(function (r) {
      var row = r.closest('.mjo-region__row');
      if (!row) return;
      var off = !!allowed && allowed.indexOf(r.value) === -1;
      row.hidden = off;
      /* inline display too: .mjo-region__row's own display rule outranks the
         UA's [hidden] { display: none } — the attribute alone leaves the row
         visible (measured on DEV, 390px proof) */
      row.style.display = off ? 'none' : '';
    });
  }

  function apply(root) {
    filterRows();
    (root || document).querySelectorAll('.mj-money[data-base]').forEach(function (el) {
      var cents = Number(el.dataset.base);
      if (!Number.isFinite(cents)) return;
      el.textContent = format(cents);
    });
    var cur = effective();
    document.querySelectorAll('[data-currency-label]').forEach(function (el) {
      el.textContent = cur;
    });
    document.querySelectorAll('[data-currency-note]').forEach(function (el) {
      el.hidden = cur === 'SGD';
    });
    document.querySelectorAll('[data-currency-note-cur]').forEach(function (el) {
      el.textContent = cur;
    });
    document.querySelectorAll('[data-currency-radio]').forEach(function (r) {
      r.checked = r.value === cur;
    });
    var submit = document.querySelector('[data-currency-submit]');
    if (submit) {
      var sel = document.querySelector('[data-currency-radio]:checked');
      submit.textContent = 'Show prices in ' + (sel ? sel.value : cur);
    }
    try {
      /* country rides along so consumers (the cart delivery meter) have one
         event and one source of truth rather than a second lookup */
      window.dispatchEvent(
        new CustomEvent('mije:currency', { detail: { currency: cur, country: geoCountry } })
      );
    } catch (e) { /* old browsers */ }
  }

  /* Which source actually produced `rates`, surfaced on <html> for debugging:
     data-mije-fx-source = airwallex_pinned | erapi_calibrated | none
     data-mije-fx-age-hours = age of the published rates (pinned source only) */
  function markSource(src, ageHours) {
    try {
      document.documentElement.setAttribute('data-mije-fx-source', src);
      document.documentElement.setAttribute('data-mije-fx-age-hours',
        ageHours == null ? '' : String(Math.round(ageHours * 10) / 10));
    } catch (e) { /* never fatal */ }
  }

  /* The er-api mid under-quotes what Airwallex actually charges by ~3.8%
     (measured 2026-08-10, audit/output/AIRWALLEX_FX_PROBE.md). If the pinned
     pipeline is dead the fallback must still over-quote rather than
     under-quote, so each mid is calibrated up by ITS OWN measured margin
     plus headroom - one number cannot serve all four: VND measured +5.2%
     against er-api (whose VND mid runs low) while AUD measured +3.4%.
     Measured 2026-08-10: MYR +3.82%, THB +3.66%, VND +5.22%, AUD +3.40%.
     AUD runs 1.040 not 1.037: the probe showed 1.037 leaves only +0.25%
     margin over the measured charge — too thin for a fallback that may
     serve for days if the cron dies. */
  var ERAPI_CALIBRATION = { MYR: 1.039, THB: 1.039, VND: 1.055, AUD: 1.040 };

  /* Rates published hourly into the shop metafield mije.fx_rates and rendered
     into the page by mije-currency-boot.liquid. `pinned` is the display rate:
     HR_24 quote x 1.01, re-pinned only on >1% drift or 14-day age, so prices
     hold still day to day while staying above the real charge (the charge is
     the HR_1 rate, ~15bps BELOW the HR_24 basis). */
  function pinnedFromMetafield() {
    try {
      var mf = window.__mijeFxRates;
      if (!mf || mf.source !== 'airwallex_hr24' || !mf.pinned || !mf.fetched_at) return null;
      var age = (Date.now() - Date.parse(mf.fetched_at)) / 36e5;
      if (!(age >= 0) || age > 26) return null; // cron dead -> calibrated fallback
      var out = {};
      for (var i = 0; i < SUPPORTED.length; i++) {
        var c = SUPPORTED[i];
        if (c === 'SGD') continue;
        if (!mf.pinned[c] || !(Number(mf.pinned[c].rate) > 0)) return null;
        out[c] = Number(mf.pinned[c].rate);
      }
      return { rates: out, age: age };
    } catch (e) { return null; }
  }

  function loadRates() {
    // 1. pinned Airwallex rates from the metafield — zero extra requests
    var pinned = pinnedFromMetafield();
    if (pinned) {
      rates = pinned.rates;
      markSource('airwallex_pinned', pinned.age);
      return Promise.resolve();
    }
    // 2. er-api mid x calibration (only when the cron is dead or absent).
    //    The `calibrated` flag invalidates any cache written before this
    //    change shipped — an uncalibrated cached rate must never serve.
    var today = new Date().toISOString().slice(0, 10);
    try {
      var cached = JSON.parse(read(RKEY) || 'null');
      if (cached && cached.date === today && cached.rates && cached.calibrated) {
        rates = cached.rates;
        markSource('erapi_calibrated', null);
        return Promise.resolve();
      }
    } catch (e) { /* fall through */ }
    return fetch('https://open.er-api.com/v6/latest/SGD')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || data.result !== 'success' || !data.rates) { markSource('none', null); return; }
        rates = {};
        for (var c in ERAPI_CALIBRATION) {
          if (!(Number(data.rates[c]) > 0)) { rates = null; markSource('none', null); return; }
          rates[c] = data.rates[c] * ERAPI_CALIBRATION[c];
        }
        store(RKEY, JSON.stringify({ date: today, rates: rates, calibrated: true }));
        markSource('erapi_calibrated', null);
      })
      .catch(function () { markSource('none', null); /* silent SGD fallback */ });
  }

  window.MijeCurrency = {
    get: current,
    effective: effective,
    format: format,
    /* Where the current currency came from — 'manual' outranks 'geo'.
       'default' means no choice and no country (not yet resolved, or the
       lookup failed) — i.e. the plain SGD fallback. */
    source: function () {
      if (chosen()) return 'manual';
      return geoCountry ? 'geo' : 'default';
    },
    /* The detected country, and the ONLY place anything in the theme should
       get it. null = not resolved yet (wait), '' = resolved to unknown (the
       lookup failed — there will be no answer this session). Independent of
       the display currency: a shopper in MY reading SGD is still in MY. */
    country: function () {
      return geoCountry;
    },
    set: function (cur) {
      if (SUPPORTED.indexOf(cur) === -1) cur = 'SGD';
      store(KEY, cur);
      if (cur !== 'SGD' && (!rates || !rates[cur])) {
        loadRates().then(function () { apply(); });
      } else {
        apply();
      }
    },
  };

  /* late-added price nodes (drawer re-renders, predictive, saved cards…) */
  new MutationObserver(function (muts) {
    if (effective() === 'SGD') return;
    muts.forEach(function (m) {
      m.addedNodes.forEach(function (n) {
        if (n.nodeType !== 1) return;
        if (n.matches && n.matches('.mj-money[data-base]')) apply(n.parentNode || document);
        else if (n.querySelector && n.querySelector('.mj-money[data-base]')) apply(n);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* Whether painting will need FX rates: any non-SGD preference, or the
     context-currency guard (foreign context while geo does not match — the
     guard can only engage once rates exist, so they must be fetched). */
  function needsRates() {
    if (current() !== 'SGD') return true;
    return !!(CTX_CURRENCY[CTX] && String(geoCountry || '') !== CTX);
  }

  /* Paint the currency we currently believe in, fetching rates if needed. */
  function refresh() {
    if (needsRates()) return loadRates().then(function () { apply(); });
    apply();
    return Promise.resolve();
  }

  /*
    Settle the country at SCRIPT time, not at DOMContentLoaded. This file is
    deferred and runs before assets/mije-cart.js, which asks for the country
    immediately to pick the delivery meter's market — a value already sitting
    in sessionStorage should not make it render the fallback and repaint a
    tick later. Costs nothing: it is one sessionStorage read.
  */
  var countrySettled = resolveCountrySync();
  syncGeoCurrency();

  function init() {
    var settled = countrySettled;
    refresh();
    /* First page view of a session: upgrade once the country lookup lands.
       Until then the visitor sees SGD — the currency they are actually
       charged, and the fallback — never a wrong non-SGD figure — and the cart
       meter shows its neutral delivery line. Every subsequent view in the
       session settles synchronously above. */
    if (!settled) {
      resolveCountry().then(function () {
        syncGeoCurrency();
        refresh();
      });
    }

    /* modal wiring */
    var modal = document.querySelector('[data-currency-modal]');
    if (!modal) return;
    var trigger = null;
    document.querySelectorAll('[data-currency-open]').forEach(function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        trigger = t;
        modal.hidden = false;
      });
    });
    modal.addEventListener('click', function (e) {
      if (e.target === modal || e.target.closest('[data-currency-close]')) {
        modal.hidden = true;
        if (trigger) trigger.focus();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) modal.hidden = true;
    });
    modal.addEventListener('change', function () {
      var sel = modal.querySelector('[data-currency-radio]:checked');
      var submit = modal.querySelector('[data-currency-submit]');
      if (sel && submit) submit.textContent = 'Show prices in ' + sel.value;
    });
    var submit = modal.querySelector('[data-currency-submit]');
    if (submit) {
      submit.addEventListener('click', function () {
        var sel = modal.querySelector('[data-currency-radio]:checked');
        window.MijeCurrency.set(sel ? sel.value : 'SGD');
        modal.hidden = true;
        if (trigger) trigger.focus();
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
